/**
 * CommunicationsPage — Administration des notifications (emails + SMS).
 * - Table: date, destinataire (masque), canal, evenement, statut, provider ID, erreur
 * - Filtres: canal, statut, evenement, dates
 * - Bouton "Renvoyer" pour emails failed (rate-limit 3)
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Mail, MessageSquare, RefreshCw, Search, CheckCircle2, XCircle, Clock, TestTube } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  sent: { label: "Envoyé", cls: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle2 },
  queued: { label: "En attente", cls: "bg-blue-100 text-blue-700 border-blue-200", icon: Clock },
  failed: { label: "Échec", cls: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  mocked: { label: "Mock (provider off)", cls: "bg-orange-100 text-orange-700 border-orange-200", icon: TestTube },
  skipped_test_mode: { label: "Ignoré (test mode)", cls: "bg-gray-100 text-gray-700 border-gray-200", icon: TestTube },
};

const CHANNELS = [
  { value: "all", label: "Tous canaux" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

const STATUSES = [
  { value: "all", label: "Tous statuts" },
  { value: "sent", label: "Envoyé" },
  { value: "queued", label: "En attente" },
  { value: "failed", label: "Échec" },
  { value: "mocked", label: "Mock" },
  { value: "skipped_test_mode", label: "Ignoré (test mode)" },
];

export default function CommunicationsPage() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("");
  const [retryTarget, setRetryTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/notifications/status`, { headers: auth() });
      setStatus(r.data);
    } catch { /* silencieux */ }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (channelFilter !== "all") params.channel = channelFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (eventFilter.trim()) params.event = eventFilter.trim();
      const r = await axios.get(`${API}/admin/notifications/logs`, { params, headers: auth() });
      setLogs(r.data || []);
    } catch (e) {
      toast.error("Erreur chargement journaux");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, [channelFilter, statusFilter]);

  const handleResend = async () => {
    if (!retryTarget) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/notifications/logs/${retryTarget.id}/resend`, {}, { headers: auth() });
      toast.success(`Renvoi ${r.data.status}`);
      setRetryTarget(null);
      await loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renvoi impossible");
    } finally { setBusy(false); }
  };

  const handleTestEmail = async () => {
    const to = prompt("Destinataire email de test (whitelist NOTIFICATIONS_TEST_RECIPIENTS) :");
    if (!to) return;
    try {
      const r = await axios.post(`${API}/admin/notifications/test-email?to=${encodeURIComponent(to)}`, {}, { headers: auth() });
      toast.success(`Test : ${r.data.status}`);
      await loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary flex items-center gap-2">
            <Mail className="h-6 w-6" /> Communications
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Journal des emails et SMS transactionnels envoyés aux adhérents.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleTestEmail} data-testid="notif-test-email-btn">
            <TestTube className="h-4 w-4 mr-2" /> Email de test
          </Button>
          <Button variant="outline" size="icon" onClick={loadLogs} title="Rafraîchir">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Provider status */}
      {status && (
        <div className="bg-white border border-border rounded-lg p-4 flex flex-wrap gap-3 text-sm">
          <StatusPill label="Email (Resend)" ok={status.email_configured} />
          <StatusPill label="SMS (Twilio)" ok={status.sms_configured} />
          <StatusPill label="URL app" ok={status.app_url_configured} />
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Mode :</span>
            <Badge className={status.test_mode ? "bg-orange-100 text-orange-700 border-orange-200" : "bg-green-100 text-green-700 border-green-200"}>
              {status.test_mode ? `Test (whitelist ${status.whitelist_size})` : "Production"}
            </Badge>
          </div>
          {status.email_from && (
            <div className="text-muted-foreground">Depuis: <span className="font-mono text-xs">{status.email_from}</span></div>
          )}
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white border border-border rounded-lg p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filtrer par événement (ex: adhesion_approved)"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadLogs(); }}
            className="pl-9"
            data-testid="notif-event-filter"
          />
        </div>
        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="md:w-48" data-testid="notif-channel-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CHANNELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="md:w-52" data-testid="notif-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            Aucun envoi pour ces critères.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-sm">
              <thead className="bg-secondary/60 text-muted-foreground text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Date</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Canal</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Destinataire</th>
                  <th className="text-left px-3 py-2">Évenement</th>
                  <th className="text-center px-3 py-2 whitespace-nowrap">Statut</th>
                  <th className="text-left px-3 py-2 hidden lg:table-cell">Provider ID</th>
                  <th className="text-center px-3 py-2 whitespace-nowrap w-12">Action</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const meta = STATUS_META[l.status] || { label: l.status, cls: "bg-gray-100 text-gray-700 border-gray-200", icon: Clock };
                  const Icon = meta.icon;
                  const canRetry = l.channel === "email" && ["failed", "mocked", "skipped_test_mode"].includes(l.status);
                  return (
                    <tr key={l.id} className="border-t border-border hover:bg-secondary/30" data-testid={`notif-row-${l.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                        {l.created_at ? new Date(l.created_at).toLocaleString("fr-FR") : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {l.channel === "email" ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Mail className="h-3 w-3 mr-1" /> Email
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            <MessageSquare className="h-3 w-3 mr-1" /> SMS
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{l.recipient_masked || "***"}</td>
                      <td className="px-3 py-2 truncate max-w-[220px]" title={l.event}>{l.event}</td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <Badge variant="outline" className={meta.cls}>
                          <Icon className="h-3 w-3 mr-1" /> {meta.label}
                        </Badge>
                        {l.attempts > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {l.attempts} tentative{l.attempts > 1 ? "s" : ""}
                            {l.manual_retries > 0 && ` (${l.manual_retries} manuel${l.manual_retries > 1 ? "s" : ""})`}
                          </div>
                        )}
                        {l.error && (
                          <div className="text-xs text-red-600 mt-1 truncate max-w-[200px]" title={l.error}>{l.error}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground hidden lg:table-cell truncate max-w-[150px]" title={l.provider_message_id || ""}>
                        {l.provider_message_id || "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {canRetry && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setRetryTarget(l)}
                            title="Renvoyer"
                            data-testid={`notif-retry-${l.id}`}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal renvoi */}
      <Dialog open={!!retryTarget} onOpenChange={(v) => { if (!v) setRetryTarget(null); }}>
        <DialogContent data-testid="notif-retry-dialog">
          <DialogHeader>
            <DialogTitle>Renvoyer cet email ?</DialogTitle>
            <DialogDescription>
              Un nouvel envoi sera tenté auprès du fournisseur. Maximum 3 renvois manuels par log.
            </DialogDescription>
          </DialogHeader>
          {retryTarget && (
            <div className="text-sm bg-secondary rounded p-3">
              <div><strong>Destinataire :</strong> {retryTarget.recipient_masked}</div>
              <div><strong>Événement :</strong> {retryTarget.event}</div>
              <div><strong>Renvois manuels déjà effectués :</strong> {retryTarget.manual_retries || 0}/3</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRetryTarget(null)}>Annuler</Button>
            <Button onClick={handleResend} disabled={busy} data-testid="notif-retry-confirm">
              <RefreshCw className="h-4 w-4 mr-2" /> Renvoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusPill({ label, ok }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">{label} :</span>
      <Badge className={ok ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}>
        {ok ? "Configuré" : "Non configuré"}
      </Badge>
    </div>
  );
}
